import React, { useEffect, useState } from 'react';
import { X } from '@phosphor-icons/react';
import {
  fetchErpExportSettings,
  saveErpExportSettings,
} from '../../api/phase2Analytics';
import { IntegrationLogo } from './IntegrationLogo';
import { cn } from '../../lib/utils';

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'manual', label: 'Manual only' },
];

/** Must match backend REQUIRED_REVENUE_TYPES (CMP product_type). */
const REQUIRED_REVENUE_TYPES = [
  { revenueType: 'admission', glAccountName: 'Admission / tickets' },
  { revenueType: 'seasoncard', glAccountName: 'Season cards' },
  { revenueType: 'added_product', glAccountName: 'Add-ons / merch' },
];

function ensureRequiredGlMapping(rows = []) {
  const byType = new Map(
    (rows || [])
      .filter((row) => row?.revenueType)
      .map((row) => [row.revenueType, row])
  );
  return REQUIRED_REVENUE_TYPES.map((required) => {
    const existing = byType.get(required.revenueType);
    return {
      revenueType: required.revenueType,
      glAccount: existing?.glAccount || '',
      glAccountName: existing?.glAccountName || required.glAccountName,
    };
  });
}

function isMappingComplete(rows = []) {
  return REQUIRED_REVENUE_TYPES.every((required) => {
    const row = (rows || []).find((r) => r.revenueType === required.revenueType);
    return Boolean(row?.glAccount?.trim());
  });
}

function formatRelative(value) {
  if (!value) return 'Never';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return 'Never';
  const deltaSec = Math.round((Date.now() - then) / 1000);
  if (deltaSec < 3600) return `${Math.max(1, Math.floor(deltaSec / 60))}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
}

/**
 * Dynamics / ERP export settings: frequency, GL mapping, dimensions, last export.
 * Persists via PATCH /api/erp/export-settings when available; mock otherwise.
 */
export function ErpExportSettingsModal({ item, accountId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('mock');
  const [frequency, setFrequency] = useState('daily');
  const [glMapping, setGlMapping] = useState(() => ensureRequiredGlMapping([]));
  const [dimensions, setDimensions] = useState([]);
  const [lastExportAt, setLastExportAt] = useState(null);
  const [lastExportStatus, setLastExportStatus] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [mappingComplete, setMappingComplete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data, source: src } = await fetchErpExportSettings({
          accountId,
          provider: item.provider || 'dynamics',
        });
        if (cancelled) return;
        const nextMapping = ensureRequiredGlMapping(data.glMapping || []);
        setSource(src);
        setFrequency(data.frequency || 'daily');
        setGlMapping(nextMapping);
        setDimensions(data.dimensions || []);
        setLastExportAt(data.lastExportAt || null);
        setLastExportStatus(data.lastExportStatus || null);
        setExportError(data.errorMessage || null);
        setMappingComplete(
          data.mappingComplete != null ? Boolean(data.mappingComplete) : isMappingComplete(nextMapping)
        );
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load ERP settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [accountId, item.provider]);

  function updateGlRow(index, field, value) {
    setGlMapping((rows) => {
      const next = rows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
      setMappingComplete(isMappingComplete(next));
      return next;
    });
  }

  function updateDimension(index, field, value) {
    setDimensions((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  }

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        provider: item.provider || 'dynamics',
        frequency,
        glMapping: ensureRequiredGlMapping(glMapping),
        dimensions,
      };
      const { data, source: src } = await saveErpExportSettings(payload, { accountId });
      setSource(src);
      setLastExportAt(data.lastExportAt || lastExportAt);
      setLastExportStatus(data.lastExportStatus || lastExportStatus);
      setMappingComplete(
        data.mappingComplete != null
          ? Boolean(data.mappingComplete)
          : isMappingComplete(payload.glMapping)
      );
      onSaved?.({ message: src === 'mock' ? 'Settings saved (demo).' : 'Export settings saved.' });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="erp-export-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-[0_24px_64px_rgba(15,23,42,0.24)]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <IntegrationLogo logoKey={item.logoKey} name={item.name} src={item.logoUrl} size="sm" />
            <div>
              <h3 id="erp-export-title" className="font-display text-sm font-bold text-[#201C35]">
                Export settings
              </h3>
              <p className="text-[11px] text-slate-500">
                {source === 'api' ? 'Live settings' : 'Demo settings'}
                {mappingComplete ? ' · mapping complete' : ' · mapping incomplete'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {loading ? (
          <p className="mt-6 text-sm text-slate-500">Loading export settings…</p>
        ) : (
          <form className="mt-4 space-y-4" onSubmit={handleSave}>
            <div className="rounded-xl bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
              Last export: {formatRelative(lastExportAt)}
              {lastExportStatus ? ` · ${lastExportStatus}` : ''}
              {exportError ? ` · ${exportError}` : ''}
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-700" htmlFor="erp-frequency">
                Sync frequency
              </label>
              <select
                id="erp-frequency"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="mt-1 h-9 w-full rounded-lg border border-slate-200 px-2 text-sm"
              >
                {FREQUENCIES.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-700">GL mapping</p>
              <p className="mt-0.5 text-[11px] text-slate-500">
                CMP product types: admission, seasoncard, added_product
              </p>
              <div className="mt-2 space-y-2">
                {glMapping.map((row, index) => (
                  <div key={row.revenueType || index} className="grid grid-cols-[1.2fr_1fr] gap-2">
                    <input
                      value={row.glAccountName || row.revenueType || ''}
                      readOnly
                      className="h-9 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-slate-700"
                      aria-label={`Revenue type ${row.revenueType}`}
                      title={row.revenueType}
                    />
                    <input
                      value={row.glAccount || ''}
                      onChange={(e) => updateGlRow(index, 'glAccount', e.target.value)}
                      className="h-9 rounded-lg border border-slate-200 px-2 text-sm"
                      placeholder="GL account"
                      aria-label={`GL account for ${row.revenueType}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-slate-700">Dimensions</p>
              <div className="mt-2 space-y-2">
                {dimensions.map((row, index) => (
                  <div key={row.key || index} className="grid grid-cols-[1fr_1fr] gap-2">
                    <input
                      value={row.sourceField || ''}
                      onChange={(e) => updateDimension(index, 'sourceField', e.target.value)}
                      className="h-9 rounded-lg border border-slate-200 px-2 text-sm"
                      placeholder="Source field"
                      aria-label={`Dimension source ${index + 1}`}
                    />
                    <input
                      value={row.erpDimension || ''}
                      onChange={(e) => updateDimension(index, 'erpDimension', e.target.value)}
                      className="h-9 rounded-lg border border-slate-200 px-2 text-sm"
                      placeholder="ERP dimension"
                      aria-label={`ERP dimension ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {error ? <p className="text-xs text-rose-600">{error}</p> : null}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 items-center rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className={cn(
                  'inline-flex h-9 items-center rounded-full bg-[var(--brand-primary,#1A428A)] px-4 text-sm font-semibold text-white shadow-md hover:bg-[#163872] disabled:opacity-50'
                )}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
